import { CommonConstants, ErrorResponse } from "@/common/CommonConstants";
import { DriverWorkShiftEditTemplate } from "@/components/driverWorkCycles/DriverWorkShiftEditTemplate";
import { ConfirmDialog } from "@/components/molecules/ConfirmDialog";
import { DriverWorkShiftEdit as EditType } from "@/models/driverWorkShiftsAndCycle";
import {
  checkEffectiveShiftAPI,
  getDriverWorkShiftAPI,
  updateDriverWorkShiftAPI,
} from "@/util/api/driverWorkShifts";
import { Messages } from "@/util/Messages";
import { useStatefulNavigator } from "@/util/useStatefulNavigator";
import { Typography } from "@mui/material";
import { AxiosError } from "axios";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  DriverWorkCyclesConstants,
  getTime,
} from "./DriverWorkCyclesConstants";
import toast from "@/components/hoc/SnackbarAlert";
import { getOfficeAPI } from "@/util/api/API";
import { useLoadingPromise } from "@/hooks/useLoadingPromise";

export const DriverWorkShiftEdit = () => {
  const params = useParams<{ office_id: string; shift_id: string }>();
  const officeId = params.office_id;
  const shiftId = params.shift_id;

  const [driverWorkShift, setDriverWorkShift] = useState<EditType>({
    isMultipleHolidayVersionRequest: true,
  });
  const [showWarning, setShowWarning] = useState(false);
  const handleWarnCancel = useCallback(() => {
    setShowWarning(false);
  }, []);

  const [loading, updateDriverWorkShiftAPIWrapper] = useLoadingPromise(
    updateDriverWorkShiftAPI
  );

  const { navigate } = useStatefulNavigator();
  const goToDetail = useCallback(() => {
    navigate(`/driver_work_cycles/${officeId}`);
  }, [navigate, officeId]);

  const update = useCallback(() => {
    if (driverWorkShift == null || loading) {
      return;
    }

    setShowWarning(false);

    const {
      weekdayOutgoingBin,
      weekdayIncomingBin,
      holidayOutgoingBin,
      holidayIncomingBin,
      ...request
    } = driverWorkShift;

    updateDriverWorkShiftAPIWrapper({
      ...request,
      weekdayOutgoingBin: !driverWorkShift.weekdayIsDayOff
        ? weekdayOutgoingBin
        : undefined,
      weekdayIncomingBin: !driverWorkShift.weekdayIsDayOff
        ? weekdayIncomingBin
        : undefined,
      holidayOutgoingBin: !driverWorkShift.holidayIsDayOff
        ? holidayOutgoingBin
        : undefined,
      holidayIncomingBin: !driverWorkShift.holidayIsDayOff
        ? holidayIncomingBin
        : undefined,
    })
      .then(goToDetail)
      .catch((e: AxiosError<ErrorResponse>) => {
        if ([404, 409].includes(e.response?.status ?? -1)) {
          toast.error(
            `${CommonConstants.Replace}${Messages.ErrorOccurred}: ${
              e?.response?.data?.message ?? ""
            }`
          );
        }
      });
  }, [driverWorkShift, goToDetail, loading, updateDriverWorkShiftAPIWrapper]);

  const prepareUpdate = useCallback(() => {
    checkEffectiveShiftAPI(Number(shiftId))
      .then((res) => {
        if (res.data) {
          setShowWarning(true);
          return;
        }

        update();
      })
      .catch((e) =>
        console.log("===Error occurred while checking DriverWorkShift===", e)
      );
  }, [shiftId, update]);

  useEffect(() => {
    if (shiftId == null || officeId == null) {
      return;
    }

    getDriverWorkShiftAPI(Number(shiftId))
      .then((res) => {
        const {
          weekdayOutgoingBin,
          weekdayIncomingBin,
          holidayOutgoingBin,
          holidayIncomingBin,
        } = res.data;

        const [weekdayStartHours, weekdayStartMinutes] = getTime(
          "from",
          weekdayOutgoingBin
        );
        const [weekdayEndHours, weekdayEndMinutes] = getTime(
          "to",
          weekdayIncomingBin
        );
        const [holidayStartHours, holidayStartMinutes] = getTime(
          "from",
          holidayOutgoingBin
        );
        const [holidayEndHours, holidayEndMinutes] = getTime(
          "to",
          holidayIncomingBin
        );

        const { weekdayIsDayOff, holidayIsDayOff } = res.data;
        const allDayOff = weekdayIsDayOff && holidayIsDayOff;
        const onlyWeekdayIsDayOff = !allDayOff && weekdayIsDayOff;
        const onlyHolidayIsDayOff = !allDayOff && holidayIsDayOff;

        getOfficeAPI(Number(officeId))
          .then((sres) => {
            setDriverWorkShift({
              ...res.data,
              offices: sres.data,
              weekdayOutgoingBin: onlyWeekdayIsDayOff ? 0 : weekdayOutgoingBin,
              weekdayIncomingBin: onlyWeekdayIsDayOff ? 0 : weekdayIncomingBin,
              weekdayStartHours: onlyWeekdayIsDayOff ? "00" : weekdayStartHours,
              weekdayStartMinutes: onlyWeekdayIsDayOff
                ? "00"
                : weekdayStartMinutes,
              weekdayEndHours: onlyWeekdayIsDayOff ? "00" : weekdayEndHours,
              weekdayEndMinutes: onlyWeekdayIsDayOff ? "00" : weekdayEndMinutes,
              holidayOutgoingBin: onlyHolidayIsDayOff ? 0 : holidayOutgoingBin,
              holidayIncomingBin: onlyHolidayIsDayOff ? 0 : holidayIncomingBin,
              holidayStartHours: onlyHolidayIsDayOff ? "00" : holidayStartHours,
              holidayStartMinutes: onlyHolidayIsDayOff
                ? "00"
                : holidayStartMinutes,
              holidayEndHours: onlyHolidayIsDayOff ? "00" : holidayEndHours,
              holidayEndMinutes: onlyHolidayIsDayOff ? "00" : holidayEndMinutes,
              isMultipleHolidayVersionRequest: true,
            });
          })
          .catch((e) =>
            console.log("===Erro occurred while getting Office===")
          );
      })
      .catch((e) => console.log("===Error occurred while getting Shift==="));
  }, [officeId, shiftId]);

  return (
    <>
      <DriverWorkShiftEditTemplate
        title={DriverWorkCyclesConstants.DriverWorkShiftEdit}
        driverWorkShift={driverWorkShift}
        loading={loading}
        onCancel={goToDetail}
        onSubmit={prepareUpdate}
        onChangeData={setDriverWorkShift}
      />
      <ConfirmDialog
        visible={showWarning}
        headerTitle={DriverWorkCyclesConstants.ConfirmTitle}
        showIcon
        type="Normal"
        onCancel={handleWarnCancel}
        onOk={update}
      >
        <Typography gutterBottom sx={{ whiteSpace: "pre-line" }}>
          {DriverWorkCyclesConstants.UpdateWarning}
        </Typography>
      </ConfirmDialog>
    </>
  );
};
